import { readCookieConsent } from './cookie-consent';

const containerId = import.meta.env.VITE_GTM_ID?.trim() ?? '';
const GOOGLE_TAG_MANAGER_SCRIPT_ID = 'eplotera-google-tag-manager';

type DataLayerEntry = Record<string, string | number | boolean>;

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
  }
}

let tagManagerInitialized = false;
let lastPageView: { path: string; location: string } | null = null;

function getDataLayer(): DataLayerEntry[] {
  window.dataLayer ??= [];
  return window.dataLayer;
}

function isValidContainerId(value: string): boolean {
  return /^GTM-[A-Z0-9]+$/i.test(value);
}

function initializeGoogleTagManager(): boolean {
  if (!isValidContainerId(containerId) || readCookieConsent() !== 'accepted') {
    return false;
  }
  if (tagManagerInitialized) return true;

  getDataLayer().push({ event: 'gtm.js', 'gtm.start': Date.now() });
  const script = document.createElement('script');
  script.id = GOOGLE_TAG_MANAGER_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
  tagManagerInitialized = true;
  return true;
}

export function trackPageView(path: string, title: string): boolean {
  if (lastPageView?.path === path || !initializeGoogleTagManager()) return false;

  const location = `${window.location.origin}${path}`;
  const pageView: DataLayerEntry = {
    event: 'page_view',
    page_location: location,
    page_path: path,
    page_title: title,
  };
  const referrer = lastPageView?.location ?? document.referrer;
  if (referrer) pageView.page_referrer = referrer;

  getDataLayer().push(pageView);
  lastPageView = { path, location };
  return true;
}

function expireCookie(cookieName: string, domain?: string): void {
  document.cookie = `${cookieName}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ''}`;
}

export function disableAnalytics(): boolean {
  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));

  for (const cookieName of new Set(cookieNames)) {
    expireCookie(cookieName);
    if (window.location.hostname === 'eplotera.com' || window.location.hostname.endsWith('.eplotera.com')) {
      expireCookie(cookieName, '.eplotera.com');
    }
  }
  lastPageView = null;
  return tagManagerInitialized;
}
